import type { Canton, District, Province, ComboBoxItem } from "./addressPickerTypes";

/**
 * JSON shape: same as costa-rica / panama codification files.
 * Level 1: provincia · Level 2: "cantón" (en PA = distrito) · Level 3: "distrito" (en PA = corregimiento).
 */
export type AddressCodesJson = {
  provinces: Record<string, string>;
  cantons: Record<string, { name: string; province: number }>;
  districts: Record<string, { name: string; canton: number }>;
};

export function createAddressRegistry(codes: AddressCodesJson) {
  const createProvinces = (): Province[] => {
    return Object.entries(codes.provinces).map(([id, name]) => ({
      id: parseInt(id, 10),
      name: name as string,
    }));
  };

  const createCantons = (): Canton[] => {
    return Object.entries(codes.cantons).map(([id, data]) => ({
      id: parseInt(id, 10),
      name: (data as { name: string }).name,
      province: (data as { province: number }).province,
    }));
  };

  const createDistricts = (): District[] => {
    return Object.entries(codes.districts).map(([id, data]) => ({
      id: parseInt(id, 10),
      name: (data as { name: string }).name,
      canton: (data as { canton: number }).canton,
    }));
  };

  const loadProvinceSelection = (
    province: string | number,
    currentSelection: Province | null
  ): Province | null => {
    if (province && province !== 0 && !currentSelection) {
      const provinceNumeric =
        typeof province === "string" ? parseInt(province, 10) : province;
      const provinceName = (codes.provinces as Record<string, string>)[
        provinceNumeric.toString()
      ];
      if (provinceName) {
        return { id: provinceNumeric, name: provinceName };
      }
    }
    return currentSelection;
  };

  const loadCantonSelection = (
    canton: string | number,
    province: string | number,
    selectedProvince: Province | null,
    currentSelection: Canton | null
  ): Canton | null => {
    if (canton && canton !== 0 && !currentSelection && selectedProvince) {
      const provinceNumeric =
        typeof province === "string" ? parseInt(province, 10) : province;
      const cantonNumeric =
        typeof canton === "string" ? parseInt(canton, 10) : canton;
      const fullCantonCode = provinceNumeric * 100 + cantonNumeric;
      const cantonData = (codes.cantons as Record<string, { name: string; province: number }>)[
        fullCantonCode.toString()
      ];
      if (cantonData) {
        return {
          id: fullCantonCode,
          name: cantonData.name,
          province: cantonData.province,
        };
      }
    }
    return currentSelection;
  };

  const loadDistrictSelection = (
    district: string | number,
    selectedCanton: Canton | null,
    currentSelection: District | null
  ): District | null => {
    if (district && district !== 0 && !currentSelection && selectedCanton) {
      const districtNumeric =
        typeof district === "string" ? parseInt(district, 10) : district;
      const fullDistrictCode = selectedCanton.id * 100 + districtNumeric;
      const districtData = (codes.districts as Record<
        string,
        { name: string; canton: number }
      >)[fullDistrictCode.toString()];
      if (districtData) {
        return {
          id: fullDistrictCode,
          name: districtData.name,
          canton: districtData.canton,
        };
      }
    }
    return currentSelection;
  };

  const resolveLocationLabelsForDb = (
    province: string | number,
    canton: string | number,
    district: string | number
  ): { province: string | null; canton: string | null; district: string | null } => {
    let provinceName: string | null = null;
    if (province !== "" && province != null && province !== 0) {
      const pid =
        typeof province === "string" ? parseInt(province, 10) : Number(province);
      if (Number.isFinite(pid) && pid > 0) {
        const v = (codes.provinces as Record<string, string>)[String(pid)];
        if (v) provinceName = String(v).trim();
      }
    }

    let cantonName: string | null = null;
    if (provinceName && canton !== "" && canton != null && canton !== 0) {
      const pn =
        typeof province === "string"
          ? parseInt(String(province), 10)
          : Number(province);
      const cn =
        typeof canton === "string" ? parseInt(String(canton), 10) : Number(canton);
      if (Number.isFinite(pn) && pn > 0 && Number.isFinite(cn) && cn >= 0) {
        const fullCant = pn * 100 + cn;
        const row = (codes.cantons as Record<string, { name: string }>)[
          String(fullCant)
        ];
        if (row?.name) cantonName = row.name.trim();
      }
    }

    let districtName: string | null = null;
    if (
      cantonName &&
      district !== "" &&
      district != null &&
      district !== 0
    ) {
      const pn =
        typeof province === "string"
          ? parseInt(String(province), 10)
          : Number(province);
      const cn =
        typeof canton === "string" ? parseInt(String(canton), 10) : Number(canton);
      const dn =
        typeof district === "string"
          ? parseInt(String(district), 10)
          : Number(district);
      if (
        Number.isFinite(pn) &&
        pn > 0 &&
        Number.isFinite(cn) &&
        cn >= 0 &&
        Number.isFinite(dn) &&
        dn >= 0
      ) {
        const fullCant = pn * 100 + cn;
        const fullDist = fullCant * 100 + dn;
        const row = (codes.districts as Record<string, { name: string }>)[
          String(fullDist)
        ];
        if (row?.name) districtName = row.name.trim();
      }
    }

    return { province: provinceName, canton: cantonName, district: districtName };
  };

  /**
   * Convierte nombres guardados en API (provincia, cantón/distrito PA, distrito/corregimiento)
   * a los códigos parciales que usa el picker (province id, cantón %100, distrito %100).
   */
  const matchLabelsToCodes = (
    provinciaLabel: string,
    cantonLabel: string,
    distritoLabel: string
  ): { province: number; canton: number; district: number } | null => {
    const dNorm = distritoLabel.trim().toLowerCase();
    if (!dNorm) return null;

    const cantonVariants = Array.from(
      new Set(
        cantonLabel
          .split(/\s*[\u00B7•]\s*/)
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );
    if (cantonVariants.length === 0) return null;

    const pNorm = provinciaLabel.trim().toLowerCase();

    let provinceFromApi = 0;
    if (pNorm) {
      for (const [id, name] of Object.entries(codes.provinces)) {
        if (String(name).trim().toLowerCase() === pNorm) {
          provinceFromApi = parseInt(id, 10);
          break;
        }
      }
    }

    for (const cTry of cantonVariants) {
      const cNorm = cTry.toLowerCase();
      let pid = provinceFromApi;
      if (!pid) {
        for (const [, row] of Object.entries(codes.cantons)) {
          const r = row as { name: string; province: number };
          if (r.name.trim().toLowerCase() === cNorm) {
            pid = r.province;
            break;
          }
        }
      }
      if (!pid) continue;

      let fullCantonId = 0;
      for (const [key, row] of Object.entries(codes.cantons)) {
        const r = row as { name: string; province: number };
        if (r.province === pid && r.name.trim().toLowerCase() === cNorm) {
          fullCantonId = parseInt(key, 10);
          break;
        }
      }
      if (!fullCantonId) continue;

      for (const [distKey, row] of Object.entries(codes.districts)) {
        const r = row as { name: string; canton: number };
        if (
          r.canton === fullCantonId &&
          r.name.trim().toLowerCase() === dNorm
        ) {
          const fullDistId = parseInt(distKey, 10);
          return {
            province: pid,
            canton: fullCantonId % 100,
            district: fullDistId % 100,
          };
        }
      }
    }

    return null;
  };

  return {
    codes,
    createProvinces,
    createCantons,
    createDistricts,
    loadProvinceSelection,
    loadCantonSelection,
    loadDistrictSelection,
    resolveLocationLabelsForDb,
    matchLabelsToCodes,
  };
}

export const provincesToComboBoxItems = (
  provinces: Province[]
): ComboBoxItem[] => {
  return provinces.map((p) => ({ label: p.name, value: p }));
};

export const cantonsToComboBoxItems = (cantons: Canton[]): ComboBoxItem[] => {
  return cantons.map((c) => ({ label: c.name, value: c }));
};

export const districtsToComboBoxItems = (districts: District[]): ComboBoxItem[] => {
  return districts.map((d) => ({ label: d.name, value: d }));
};

export const getProvinceComboBoxValue = (
  selectedProvince: Province | null
): ComboBoxItem | null => {
  return selectedProvince
    ? { label: selectedProvince.name, value: selectedProvince }
    : null;
};

export const getCantonComboBoxValue = (
  selectedCanton: Canton | null
): ComboBoxItem | null => {
  return selectedCanton
    ? { label: selectedCanton.name, value: selectedCanton }
    : null;
};

export const getDistrictComboBoxValue = (
  selectedDistrict: District | null
): ComboBoxItem | null => {
  return selectedDistrict
    ? { label: selectedDistrict.name, value: selectedDistrict }
    : null;
};
